import { PushNotifications } from '@capacitor/push-notifications';
import { Device } from '@capacitor/device';
import { IS_MOBILE_APP } from '@/constants/env';
import { supabase } from '@/lib/supabase';
import { logger } from '@/lib/logger';
import { 
  initializeFirebaseMessaging, 
  getFCMToken, 
  setupForegroundMessageListener 
} from '@/lib/firebase';

export interface DeviceTokenData {
  device_token: string;
  device_type: 'android' | 'ios' | 'web';
  app_version?: string;
  device_info?: {
    model?: string;
    platform?: string;
    osVersion?: string;
  };
}

/**
 * Service for managing push notifications (FCM)
 */
export class PushNotificationService {
  private static isInitialized = false;
  private static registrationToken: string | null = null;

  /**
   * Initialize push notifications and register FCM token
   * Supports both mobile (Capacitor) and web (Firebase JS SDK)
   */
  static async initialize(userId: string): Promise<boolean> {
    if (this.isInitialized) {
      logger.debug('Already initialized', { component: 'PushNotificationService' });
      return true;
    }

    if (IS_MOBILE_APP) {
      return await this.initializeMobile(userId);
    } else {
      return await this.initializeWeb(userId);
    }
  }

  /**
   * Initialize push notifications for mobile (Capacitor)
   */
  private static async initializeMobile(userId: string): Promise<boolean> {
    try {
      // Request permissions
      const permission = await PushNotifications.requestPermissions();

      if (permission.receive !== 'granted') {
        logger.warn('Permission not granted', { data: { permission }, component: 'PushNotificationService' });
        return false;
      }

      // Set up listeners BEFORE register() so we catch the token event
      // Use a promise to wait for the FCM token registration
      const tokenPromise = new Promise<boolean>((resolve) => {
        const timeout = setTimeout(() => {
          logger.warn('Token registration timed out', { component: 'PushNotificationService' });
          resolve(false);
        }, 10000);

        PushNotifications.addListener('registration', async (token) => {
          clearTimeout(timeout);
          logger.info('Registration token received', { data: { token: token.value }, component: 'PushNotificationService' });
          this.registrationToken = token.value;
          await this.registerTokenWithBackend(userId, token.value);
          resolve(true);
        });

        PushNotifications.addListener('registrationError', (error) => {
          clearTimeout(timeout);
          logger.error('Registration error', { error, component: 'PushNotificationService' });
          resolve(false);
        });
      });

      // Create notification channel for Android 8+
      await this.ensureNotificationChannel();

      // Set up notification listeners (foreground + tap)
      this.setupNotificationListeners();

      // Register for push notifications (triggers the registration event)
      await PushNotifications.register();

      // Wait for the token to be received and saved
      const tokenRegistered = await tokenPromise;

      this.isInitialized = true;
      logger.info('Initialized successfully (Mobile)', { data: { tokenRegistered }, component: 'PushNotificationService' });
      return true;
    } catch (error) {
      logger.error('Initialization error', { error, component: 'PushNotificationService' });
      return false;
    }
  }

  /**
   * Initialize push notifications for web (Firebase JS SDK)
   */
  private static async initializeWeb(userId: string): Promise<boolean> {
    try {
      // Check browser support
      if (!('Notification' in window) || !('serviceWorker' in navigator)) {
        logger.warn('Push notifications not supported in this browser', { component: 'PushNotificationService' });
        return false;
      }

      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        logger.warn('Permission not granted', { data: { permission }, component: 'PushNotificationService' });
        return false;
      }

      // Register service worker for Firebase messaging
      if ('serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
            scope: '/',
          });
          logger.debug('Service worker registered', { data: { registration }, component: 'PushNotificationService' });
        } catch (error) {
          logger.error('Service worker registration failed', { error, component: 'PushNotificationService' });
          // Try fallback service worker
          try {
            await navigator.serviceWorker.register('/sw.js', { scope: '/' });
          } catch (fallbackError) {
            logger.error('Fallback service worker registration failed', { error: fallbackError, component: 'PushNotificationService' });
            return false;
          }
        }
      }

      // Initialize Firebase Messaging
      const messaging = initializeFirebaseMessaging();
      if (!messaging) {
        logger.error('Failed to initialize Firebase Messaging', { component: 'PushNotificationService' });
        return false;
      }

      // Get FCM token
      const token = await getFCMToken();
      if (!token) {
        logger.error('Failed to get FCM token', { component: 'PushNotificationService' });
        return false;
      }

      this.registrationToken = token;
      await this.registerTokenWithBackend(userId, token, 'web');

      // Set up foreground message listener
      setupForegroundMessageListener((payload) => {
        logger.debug('Foreground message received', { data: { payload }, component: 'PushNotificationService' });
        // Show in-app notification or handle as needed
        if (payload.notification) {
          new Notification(payload.notification.title || 'New Notification', {
            body: payload.notification.body,
            icon: payload.notification.icon || '/kirana360-favicon-32x32.png',
            data: payload.data,
          });
        }
      });

      this.isInitialized = true;
      logger.info('Initialized successfully (Web)', { component: 'PushNotificationService' });
      return true;
    } catch (error) {
      logger.error('Web initialization error', { error, component: 'PushNotificationService' });
      return false;
    }
  }

  /**
   * Create the notification channel for Android 8+ (required for local notifications)
   */
  private static async ensureNotificationChannel(): Promise<void> {
    try {
      const { LocalNotifications } = await import('@capacitor/local-notifications');
      await LocalNotifications.createChannel({
        id: 'munshiji_default',
        name: 'MunshiJi Notifications',
        description: 'Order updates and store alerts',
        importance: 5, // IMPORTANCE_HIGH
        sound: 'default',
        vibration: true,
      });
      logger.debug('Notification channel created', { component: 'PushNotificationService' });
    } catch (error) {
      logger.warn('Failed to create notification channel', { error, component: 'PushNotificationService' });
    }
  }

  /**
   * Set up notification display and tap listeners (not registration — that's handled in initializeMobile)
   */
  private static setupNotificationListeners() {
    // Handle incoming notifications (when app is in foreground)
    PushNotifications.addListener('pushNotificationReceived', async (notification) => {
      logger.debug('Foreground notification received', { data: { notification }, component: 'PushNotificationService' });
      // Show as a local notification so user sees it even when app is open
      try {
        const { LocalNotifications } = await import('@capacitor/local-notifications');
        await LocalNotifications.schedule({
          notifications: [{
            title: notification.title || 'MunshiJi.AI',
            body: notification.body || '',
            id: Math.floor(Math.random() * 2147483647),
            channelId: 'munshiji_default',
            sound: 'default',
            extra: notification.data,
          }],
        });
      } catch (error) {
        logger.warn('Failed to show foreground notification', { error, component: 'PushNotificationService' });
      }
    });

    // Handle notification actions (when user taps notification)
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      logger.info('Notification action performed', { data: { action }, component: 'PushNotificationService' });
      const data = action.notification.data;
      if (data?.type === 'new_order' && data?.order_id && data?.store_id) {
        window.location.href = `/stores/${data.store_id}/invoices/${data.order_id}/edit`;
      } else if (data?.store_id) {
        window.location.href = `/stores/${data.store_id}`;
      } else {
        // Default: open dashboard
        window.location.href = '/';
      }
    });
  }

  /**
   * Register FCM token with backend
   */
  private static async registerTokenWithBackend(
    userId: string,
    token: string,
    deviceTypeOverride?: 'android' | 'ios' | 'web'
  ): Promise<void> {
    try {
      let deviceType: 'android' | 'ios' | 'web' = 'web';
      let deviceInfo: DeviceTokenData['device_info'] = {};

      if (IS_MOBILE_APP) {
        // Get device information for mobile
        const deviceInfoObj = await Device.getInfo();
        deviceType = deviceTypeOverride || (deviceInfoObj.platform === 'ios' ? 'ios' : 'android');
        deviceInfo = {
          model: deviceInfoObj.model,
          platform: deviceInfoObj.platform,
          osVersion: deviceInfoObj.osVersion,
        };
      } else {
        // Web device information
        deviceType = 'web';
        deviceInfo = {
          platform: navigator.platform,
          model: navigator.userAgent,
          osVersion: navigator.userAgent,
        };
      }
      
      const tokenData: DeviceTokenData = {
        device_token: token,
        device_type: deviceType,
        app_version: import.meta.env.VITE_APP_VERSION,
        device_info: deviceInfo,
      };

      // Upsert device token in database
      const { error } = await supabase
        .from('device_tokens')
        .upsert(
          {
            user_id: userId,
            ...tokenData,
            is_active: true,
            last_used_at: new Date().toISOString(),
          },
          {
            onConflict: 'user_id,device_token',
          }
        );

      if (error) {
        logger.error('Error registering token', { error, component: 'PushNotificationService' });
        throw error;
      }

      logger.info('Token registered successfully', { component: 'PushNotificationService' });
    } catch (error) {
      logger.error('Failed to register token', { error, component: 'PushNotificationService' });
    }
  }

  /**
   * Get current registration token
   */
  static getRegistrationToken(): string | null {
    return this.registrationToken;
  }

  /**
   * Force re-initialization (useful for retry scenarios)
   * Resets the initialized flag and attempts to get token again
   */
  static async forceReinitialize(userId: string): Promise<boolean> {
    // Reset initialization state
    this.isInitialized = false;
    this.registrationToken = null;
    
    // Try to initialize again
    return await this.initialize(userId);
  }

  /**
   * Manually get and register token (for web when permission already granted)
   */
  static async getTokenManually(userId: string): Promise<string | null> {
    if (IS_MOBILE_APP) {
      // For mobile, just return existing token or try to initialize
      if (this.registrationToken) {
        return this.registrationToken;
      }
      return null;
    }

    // For web: Try to get token directly
    try {
      const messaging = initializeFirebaseMessaging();
      if (!messaging) {
        return null;
      }

      const token = await getFCMToken();
      if (token) {
        this.registrationToken = token;
        await this.registerTokenWithBackend(userId, token, 'web');
        return token;
      }
    } catch (error) {
      logger.error('Error getting token manually', { error, component: 'PushNotificationService' });
    }

    return null;
  }

  /**
   * Unregister push notifications
   */
  static async unregister(): Promise<void> {
    try {
      if (this.registrationToken) {
        // Mark token as inactive in database
        const { error } = await supabase
          .from('device_tokens')
          .update({ is_active: false })
          .eq('device_token', this.registrationToken);

        if (error) {
          logger.error('Error unregistering token', { error, component: 'PushNotificationService' });
        }

        this.registrationToken = null;
      }

      // Remove all listeners
      await PushNotifications.removeAllListeners();
      this.isInitialized = false;
      logger.info('Unregistered successfully', { component: 'PushNotificationService' });
    } catch (error) {
      logger.error('Error unregistering', { error, component: 'PushNotificationService' });
    }
  }

  /**
   * Check if push notifications are available
   */
  static async checkPermissions(): Promise<{ receive: 'granted' | 'denied' | 'prompt' }> {
    if (IS_MOBILE_APP) {
      try {
        const result = await PushNotifications.checkPermissions();
        // Map 'prompt-with-rationale' to 'prompt'
        const receive = result.receive === 'prompt-with-rationale' ? 'prompt' : 
                       (result.receive === 'granted' ? 'granted' : 'denied');
        return { receive };
      } catch (error) {
        logger.error('Error checking permissions', { error, component: 'PushNotificationService' });
        return { receive: 'denied' };
      }
    } else {
      // Web: Check Notification API permission
      if (!('Notification' in window)) {
        return { receive: 'denied' };
      }
      
      const permission = Notification.permission;
      if (permission === 'granted') {
        return { receive: 'granted' as const };
      } else if (permission === 'denied') {
        return { receive: 'denied' as const };
      } else {
        return { receive: 'prompt' as const };
      }
    }
  }
}

